import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, schoolsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  User, Users, Baby, School, Phone, Clock,
  ChevronRight, ChevronLeft, CheckCircle, Loader2, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Parent Onboarding Wizard (Phase 3)
 * 6-step wizard: Primary Parent → Parent 2 → Children → School → Nanny → Reminder Time
 */

const STEPS = [
  { id: 1, name: 'You', icon: User },
  { id: 2, name: 'Partner', icon: Users },
  { id: 3, name: 'Child', icon: Baby },
  { id: 4, name: 'School', icon: School },
  { id: 5, name: 'Caregiver', icon: Phone },
  { id: 6, name: 'Done', icon: Clock },
];

// Conversational heading + sub for each step
const STEP_COPY = [
  {
    heading: 'Let\'s start with you',
    sub: 'Your details for daily updates and notifications.',
  },
  {
    heading: 'Any other parent to keep in the loop?',
    sub: 'Totally optional — skip this if it\'s just you.',
  },
  {
    heading: 'Tell us about your child',
    sub: 'Their name and age help us personalise everything.',
  },
  {
    heading: 'Which school do they go to?',
    sub: 'We\'ll monitor school emails and flag what matters.',
  },
  {
    heading: 'Who picks them up every day?',
    sub: 'Your caregiver will get a friendly WhatsApp check-in at pickup.',
  },
  {
    heading: 'One last thing — when do updates work for you?',
    sub: 'We\'ll send your evening summary around this time.',
  },
];

// Normalize Indian phone number to +91XXXXXXXXXX
const normalizeIndianPhone = (phone) => {
  let cleaned = phone.replace(/\s+/g, '').replace(/[^0-9+]/g, '');
  
  // Remove leading zeros
  cleaned = cleaned.replace(/^0+/, '');
  
  // Handle +91 prefix
  if (cleaned.startsWith('+91')) {
    cleaned = cleaned.substring(3);
  } else if (cleaned.startsWith('91') && cleaned.length > 10) {
    cleaned = cleaned.substring(2);
  }
  
  // Must be 10 digits now
  if (cleaned.length === 10) {
    return `+91${cleaned}`;
  }
  
  return null; // Invalid
};

export const ParentOnboarding = () => {
  const { familyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [schools, setSchools] = useState([]);
  const [errors, setErrors] = useState({});

  // Form data for all steps
  const [primaryParent, setPrimaryParent] = useState({
    relationship: '',
    full_name: '',
    whatsapp_phone: '',
  });

  const [secondaryParent, setSecondaryParent] = useState({
    enabled: false,
    relationship: '',
    full_name: '',
    whatsapp_phone: '',
    email: '',
  });

  const [children, setChildren] = useState([{
    first_name: '',
    last_name: '',
    dob: '',
  }]);

  const [schoolData, setSchoolData] = useState({
    school_id: '',
    branch_id: '',
    curriculum_track_id: '',
    class_name: '',
    school_not_listed: false,
    pending_school_name: '',
  });
  const [branches, setBranches] = useState([]);
  const [curriculumTracks, setCurriculumTracks] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingTracks, setLoadingTracks] = useState(false);

  const [nanny, setNanny] = useState({
    full_name: '',
    whatsapp_phone: '',
  });

  const [reminderTime, setReminderTime] = useState('19:30');

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        
        // Load family info
        const familyRes = await fetch(`${API}/parent/home/${familyId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        const familyData = await familyRes.json();
        
        if (familyData.ok) {
          setFamilyName(familyData.family?.family_name || 'Your Family');
          if (familyData.family?.reminder_time) {
            setReminderTime(familyData.family.reminder_time);
          }
        }

        // Load schools list
        const schoolsRes = await fetch(`${API}/schools`);
        const schoolsData = await schoolsRes.json();
        if (Array.isArray(schoolsData)) {
          setSchools(schoolsData);
        }

      } catch (err) {
        console.error('Load data error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (familyId) {
      loadData();
    }
  }, [familyId]);

  // Auto-select when only one branch/track option
  useEffect(() => {
    if (!loadingBranches && branches.length === 1 && schoolData.branch_id !== branches[0].id) {
      handleBranchChange(branches[0].id);
    }
  }, [branches, loadingBranches]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loadingTracks && curriculumTracks.length === 1 && schoolData.curriculum_track_id !== curriculumTracks[0].id) {
      setSchoolData(s => ({ ...s, curriculum_track_id: curriculumTracks[0].id }));
    }
  }, [curriculumTracks, loadingTracks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load branches when school is selected
  const handleSchoolChange = async (schoolId) => {
    setSchoolData(s => ({ ...s, school_id: schoolId, branch_id: '', curriculum_track_id: '' }));
    setBranches([]);
    setCurriculumTracks([]);
    if (!schoolId) return;
    try {
      setLoadingBranches(true);
      const res = await schoolsAPI.getBranches(schoolId);
      if (res.data?.branches) setBranches(res.data.branches);
    } catch (e) {
      // Non-critical — user can still proceed without branch selection
    } finally {
      setLoadingBranches(false);
    }
  };

  // Load curriculum tracks when branch is selected
  const handleBranchChange = async (branchId) => {
    setSchoolData(s => ({ ...s, branch_id: branchId, curriculum_track_id: '' }));
    setCurriculumTracks([]);
    if (!branchId) return;
    try {
      setLoadingTracks(true);
      const res = await schoolsAPI.getCurriculumTracks(branchId);
      if (res.data?.tracks) setCurriculumTracks(res.data.tracks);
    } catch (e) {
      // Non-critical
    } finally {
      setLoadingTracks(false);
    }
  };

  // Validation per step
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!primaryParent.relationship) newErrors.relationship = 'Select relationship';
      if (!primaryParent.full_name?.trim()) newErrors.full_name = 'Name is required';
      if (!primaryParent.whatsapp_phone?.trim()) {
        newErrors.whatsapp_phone = 'WhatsApp number is required';
      } else {
        const normalized = normalizeIndianPhone(primaryParent.whatsapp_phone);
        if (!normalized) newErrors.whatsapp_phone = 'Enter valid 10-digit Indian number';
      }
    }

    if (step === 2 && secondaryParent.enabled) {
      if (!secondaryParent.relationship) newErrors.p2_relationship = 'Select relationship';
      if (!secondaryParent.full_name?.trim()) newErrors.p2_full_name = 'Name is required';
    }

    if (step === 3) {
      const validChildren = children.filter(c => c.first_name?.trim());
      if (validChildren.length === 0) {
        newErrors.children = 'At least one child is required';
      }
    }

    if (step === 4 && !schoolData.school_not_listed) {
      if (!schoolData.school_id) newErrors.school = 'Select a school';
    }

    if (step === 5) {
      if (!nanny.full_name?.trim()) newErrors.nanny_name = 'Nanny name is required';
      if (!nanny.whatsapp_phone?.trim()) {
        newErrors.nanny_phone = 'WhatsApp number is required';
      } else {
        const normalized = normalizeIndianPhone(nanny.whatsapp_phone);
        if (!normalized) newErrors.nanny_phone = 'Enter valid 10-digit Indian number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleComplete = async () => {
    if (!validateStep(currentStep)) return;

    setSaving(true);
    const token = localStorage.getItem('access_token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    try {
      // Build the single submission payload
      const payload = {
        primary_parent: {
          full_name: primaryParent.full_name.trim(),
          relationship: primaryParent.relationship,
          whatsapp_phone: primaryParent.whatsapp_phone.trim()
        },
        secondary_parent: secondaryParent.enabled && secondaryParent.full_name ? {
          full_name: secondaryParent.full_name.trim(),
          relationship: secondaryParent.relationship || 'parent',
          whatsapp_phone: secondaryParent.whatsapp_phone?.trim() || null,
          email: secondaryParent.email?.trim() || null
        } : null,
        children: children
          .filter(c => c.first_name?.trim())
          .map(c => ({
            first_name: c.first_name.trim(),
            last_name: c.last_name?.trim() || null,
            dob: c.dob || null
          })),
        school_id: schoolData.school_not_listed ? null : (schoolData.school_id || null),
        branch_id: schoolData.branch_id || null,
        curriculum_track_id: schoolData.curriculum_track_id || null,
        class_id: schoolData.class_name || null,
        caregiver: {
          full_name: nanny.full_name.trim(),
          whatsapp_phone: nanny.whatsapp_phone.trim()
        },
        reminder_time: reminderTime
      };

      // Single API call to complete onboarding
      const response = await fetch(`${API}/parent/onboarding/${familyId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle 409 conflict (already onboarded)
        if (response.status === 409) {
          toast.info('Family already set up. Redirecting to dashboard...');
          navigate(`/parent?family_id=${familyId}`);
          return;
        }
        throw new Error(result.detail || 'Failed to complete onboarding');
      }

      // Verify status is 'active' before redirecting
      if (result.status !== 'active') {
        console.warn('Warning: Family status not active after onboarding:', result.status);
        toast.error('Setup partially completed. Please contact support.');
        return;
      }

      // If school not listed, log the pending request
      if (schoolData.school_not_listed && schoolData.pending_school_name) {
        try {
          await fetch(`${API}/events`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              event_type: 'pending_school_request',
              family_id: familyId,
              metadata: {
                school_name: schoolData.pending_school_name,
                class_name: schoolData.class_name
              }
            })
          });
        } catch (e) {
          // Non-critical, log but don't fail
          console.warn('Could not log school request event:', e);
        }
      }

      // Success! Show success message and redirect
      toast.success('Setup complete! Welcome to Kivo');
      
      // Small delay for success message visibility
      setTimeout(() => {
        navigate(`/parent?family_id=${familyId}`);
      }, 500);

    } catch (err) {
      console.error('Onboarding error:', err);
      toast.error(err.message || 'Setup failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addChild = () => {
    setChildren(prev => [...prev, { first_name: '', last_name: '', dob: '' }]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      setChildren(prev => prev.filter((_, i) => i !== index));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5E6D3] via-[#FAFAF8] to-[#F2F7F5]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5E6D3] via-[#FAFAF8] to-[#F2F7F5] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 text-center mb-1 font-heading">
            Set up {familyName}
          </h1>
          <p className="text-slate-400 text-center text-sm mb-5">
            Step {currentStep} of 6
          </p>

          {/* Progress bar */}
          <div className="h-1.5 bg-slate-100 rounded-full mb-6 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 6) * 100}%` }}
            />
          </div>

          {/* Progress Steps */}
          <div className="flex justify-between items-center">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`flex flex-col items-center ${
                  step.id === currentStep
                    ? 'text-primary'
                    : step.id < currentStep
                    ? 'text-primary'
                    : 'text-slate-300'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  step.id === currentStep
                    ? 'bg-primary-100 ring-2 ring-primary ring-offset-2'
                    : step.id < currentStep
                    ? 'bg-primary-100'
                    : 'bg-slate-100'
                }`}>
                  {step.id < currentStep ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-xs mt-1 hidden sm:block font-medium">{step.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-heading font-bold text-slate-900">
              {STEP_COPY[currentStep - 1].heading}
            </CardTitle>
            <CardDescription className="text-slate-500 text-sm">
              {STEP_COPY[currentStep - 1].sub}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Step 1: Primary Parent */}
            {currentStep === 1 && (
              <>
                <div className="space-y-2">
                  <Label className="text-slate-600">You are… <span className="text-red-400">*</span></Label>
                  <select
                    value={primaryParent.relationship}
                    onChange={(e) => setPrimaryParent(p => ({ ...p, relationship: e.target.value }))}
                    className={`w-full px-3 py-2.5 border rounded-xl text-slate-800 ${errors.relationship ? 'border-red-400' : 'border-slate-200'} focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary`}
                    data-testid="primary-relationship"
                  >
                    <option value="">Select…</option>
                    <option value="father">Father</option>
                    <option value="mother">Mother</option>
                    <option value="guardian">Guardian</option>
                  </select>
                  {errors.relationship && <p className="text-sm text-red-500">{errors.relationship}</p>}
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Your name <span className="text-red-400">*</span></Label>
                  <Input
                    value={primaryParent.full_name}
                    onChange={(e) => setPrimaryParent(p => ({ ...p, full_name: e.target.value }))}
                    placeholder="e.g. Priya Sharma"
                    className={`rounded-xl ${errors.full_name ? 'border-red-400' : ''}`}
                    data-testid="primary-name"
                  />
                  {errors.full_name && <p className="text-sm text-red-500">{errors.full_name}</p>}
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Your WhatsApp number <span className="text-red-400">*</span></Label>
                  <Input
                    value={primaryParent.whatsapp_phone}
                    onChange={(e) => setPrimaryParent(p => ({ ...p, whatsapp_phone: e.target.value }))}
                    placeholder="10-digit number, e.g. 9876543210"
                    className={`rounded-xl ${errors.whatsapp_phone ? 'border-red-400' : ''}`}
                    data-testid="primary-phone"
                  />
                  {errors.whatsapp_phone && <p className="text-sm text-red-500">{errors.whatsapp_phone}</p>}
                  <p className="text-xs text-slate-400">India numbers only — we'll add +91 automatically.</p>
                </div>
              </>
            )}

            {/* Step 2: Secondary Parent (Optional) */}
            {currentStep === 2 && (
              <>
                <label
                  htmlFor="add-parent2"
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    secondaryParent.enabled ? 'border-primary bg-primary-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    id="add-parent2"
                    checked={secondaryParent.enabled}
                    onChange={(e) => setSecondaryParent(p => ({ ...p, enabled: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 accent-primary"
                    data-testid="add-secondary-parent"
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Yes, add another parent</p>
                    <p className="text-xs text-slate-500 mt-0.5">They'll receive the same daily updates on WhatsApp.</p>
                  </div>
                </label>

                {secondaryParent.enabled && (
                  <div className="space-y-4 mt-2">
                    <div className="space-y-2">
                      <Label className="text-slate-600">They are…</Label>
                      <select
                        value={secondaryParent.relationship}
                        onChange={(e) => setSecondaryParent(p => ({ ...p, relationship: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        data-testid="secondary-relationship"
                      >
                        <option value="">Select…</option>
                        <option value="father">Father</option>
                        <option value="mother">Mother</option>
                        <option value="guardian">Guardian</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600">Their name</Label>
                      <Input
                        value={secondaryParent.full_name}
                        onChange={(e) => setSecondaryParent(p => ({ ...p, full_name: e.target.value }))}
                        placeholder="e.g. Rahul Sharma"
                        className="rounded-xl"
                        data-testid="secondary-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600">Their WhatsApp number <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input
                        value={secondaryParent.whatsapp_phone}
                        onChange={(e) => setSecondaryParent(p => ({ ...p, whatsapp_phone: e.target.value }))}
                        placeholder="10-digit number"
                        className="rounded-xl"
                        data-testid="secondary-phone"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600">Their email <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input
                        type="email"
                        value={secondaryParent.email}
                        onChange={(e) => setSecondaryParent(p => ({ ...p, email: e.target.value }))}
                        placeholder="email@example.com"
                        className="rounded-xl"
                        data-testid="secondary-email"
                      />
                    </div>
                  </div>
                )}

                {!secondaryParent.enabled && (
                  <p className="text-slate-400 text-sm text-center py-3">
                    No worries — you can always add someone later in Settings.
                  </p>
                )}
              </>
            )}

            {/* Step 3: Children */}
            {currentStep === 3 && (
              <>
                {errors.children && (
                  <p className="text-sm text-red-500 mb-2">{errors.children}</p>
                )}

                {children.map((child, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-xl space-y-4 bg-slate-50/50">
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-semibold text-slate-600">
                        {index === 0 ? 'Your child' : `Child ${index + 1}`}
                      </p>
                      {children.length > 1 && (
                        <button
                          onClick={() => removeChild(index)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600 text-xs">First name <span className="text-red-400">*</span></Label>
                      <Input
                        value={child.first_name}
                        onChange={(e) => {
                          const newChildren = [...children];
                          newChildren[index].first_name = e.target.value;
                          setChildren(newChildren);
                        }}
                        placeholder="e.g. Arya"
                        className="rounded-xl"
                        data-testid={`child-${index}-first-name`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600 text-xs">Last name <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input
                        value={child.last_name}
                        onChange={(e) => {
                          const newChildren = [...children];
                          newChildren[index].last_name = e.target.value;
                          setChildren(newChildren);
                        }}
                        placeholder="Family name"
                        className="rounded-xl"
                        data-testid={`child-${index}-last-name`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600 text-xs">Date of birth <span className="text-slate-400 font-normal">(helps personalise activity suggestions)</span></Label>
                      <Input
                        type="date"
                        value={child.dob}
                        onChange={(e) => {
                          const newChildren = [...children];
                          newChildren[index].dob = e.target.value;
                          setChildren(newChildren);
                        }}
                        className="rounded-xl"
                        data-testid={`child-${index}-dob`}
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addChild}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-200 rounded-xl text-sm text-slate-500 hover:border-primary hover:text-primary transition-colors"
                  data-testid="add-child-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add another child
                </button>
              </>
            )}

            {/* Step 4: School */}
            {currentStep === 4 && (
              <>
                {!schoolData.school_not_listed ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-slate-600">School name <span className="text-red-400">*</span></Label>
                      <select
                        value={schoolData.school_id}
                        onChange={(e) => handleSchoolChange(e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.school ? 'border-red-400' : 'border-slate-200'}`}
                        data-testid="school-select"
                      >
                        <option value="">Select school…</option>
                        {schools.map((school) => (
                          <option key={school.id} value={school.id}>
                            {school.name} {school.branch ? `(${school.branch})` : ''}
                          </option>
                        ))}
                      </select>
                      {errors.school && <p className="text-sm text-red-500">{errors.school}</p>}
                    </div>

                    {/* Branch selector — only shown if school has multiple branches */}
                    {loadingBranches && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading branches...
                      </div>
                    )}
                    {!loadingBranches && branches.length > 1 && (
                      <div className="space-y-2">
                        <Label className="text-slate-600">Campus / Branch</Label>
                        <select
                          value={schoolData.branch_id}
                          onChange={(e) => handleBranchChange(e.target.value)}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          data-testid="branch-select"
                        >
                          <option value="">Select campus…</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {/* Single-branch families: auto-selected via useEffect, no selector shown */}

                    {/* Curriculum track selector — only shown if branch has multiple tracks */}
                    {loadingTracks && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading curriculum...
                      </div>
                    )}
                    {!loadingTracks && curriculumTracks.length > 1 && (
                      <div className="space-y-2">
                        <Label className="text-slate-600">Curriculum</Label>
                        <select
                          value={schoolData.curriculum_track_id}
                          onChange={(e) => setSchoolData(s => ({ ...s, curriculum_track_id: e.target.value }))}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          data-testid="curriculum-select"
                        >
                          <option value="">Select curriculum…</option>
                          {curriculumTracks.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.curriculum_type}{t.grade_system ? ` (${t.grade_system})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {/* Single-track schools: auto-selected via useEffect, no selector shown */}

                    <div className="space-y-2">
                      <Label className="text-slate-600">Class / Grade</Label>
                      <Input
                        value={schoolData.class_name}
                        onChange={(e) => setSchoolData(s => ({ ...s, class_name: e.target.value }))}
                        placeholder="e.g. Grade 3, Nursery, LKG"
                        className="rounded-xl"
                        data-testid="class-input"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-600">School name</Label>
                      <Input
                        value={schoolData.pending_school_name}
                        onChange={(e) => setSchoolData(s => ({ ...s, pending_school_name: e.target.value }))}
                        placeholder="Type your school name"
                        className="rounded-xl"
                        data-testid="pending-school-name"
                      />
                      <p className="text-xs text-amber-600 font-medium">
                        We'll add this school and let you know when it's ready.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600">Class / Grade</Label>
                      <Input
                        value={schoolData.class_name}
                        onChange={(e) => setSchoolData(s => ({ ...s, class_name: e.target.value }))}
                        placeholder="e.g. Grade 3, Nursery"
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                )}

                <label htmlFor="school-not-listed" className="flex items-center gap-2 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    id="school-not-listed"
                    checked={schoolData.school_not_listed}
                    onChange={(e) => setSchoolData(s => ({
                      ...s,
                      school_not_listed: e.target.checked,
                      school_id: ''
                    }))}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-sm text-slate-500">My school isn't listed</span>
                </label>
              </>
            )}

            {/* Step 5: Nanny */}
            {currentStep === 5 && (
              <>
                <div className="space-y-2">
                  <Label className="text-slate-600">Their name <span className="text-red-400">*</span></Label>
                  <Input
                    value={nanny.full_name}
                    onChange={(e) => setNanny(n => ({ ...n, full_name: e.target.value }))}
                    placeholder="e.g. Sunita Devi"
                    className={`rounded-xl ${errors.nanny_name ? 'border-red-400' : ''}`}
                    data-testid="nanny-name"
                  />
                  {errors.nanny_name && <p className="text-sm text-red-500">{errors.nanny_name}</p>}
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Their WhatsApp number <span className="text-red-400">*</span></Label>
                  <Input
                    value={nanny.whatsapp_phone}
                    onChange={(e) => setNanny(n => ({ ...n, whatsapp_phone: e.target.value }))}
                    placeholder="10-digit number, e.g. 9876543210"
                    className={`rounded-xl ${errors.nanny_phone ? 'border-red-400' : ''}`}
                    data-testid="nanny-phone"
                  />
                  {errors.nanny_phone && <p className="text-sm text-red-500">{errors.nanny_phone}</p>}
                  <p className="text-xs text-slate-400">
                    Pickup check-ins and daily activity packs go to this number.
                  </p>
                </div>
              </>
            )}

            {/* Step 6: Reminder Time */}
            {currentStep === 6 && (
              <>
                <div className="space-y-2">
                  <Label className="text-slate-600">Best time for your evening summary</Label>
                  <select
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    data-testid="reminder-time-select"
                  >
                    <option value="18:00">6:00 PM</option>
                    <option value="18:30">6:30 PM</option>
                    <option value="19:00">7:00 PM</option>
                    <option value="19:30">7:30 PM — Recommended</option>
                    <option value="20:00">8:00 PM</option>
                    <option value="20:30">8:30 PM</option>
                    <option value="21:00">9:00 PM</option>
                  </select>
                  <p className="text-xs text-slate-400">India Standard Time (IST). Includes school updates and next-day prep.</p>
                </div>

                <div className="bg-primary-50 border border-primary-100 rounded-2xl p-5 mt-2 text-center">
                  <p className="text-3xl mb-2">🎉</p>
                  <h4 className="font-heading font-bold text-primary text-lg mb-1">You're all set!</h4>
                  <p className="text-sm text-slate-600">
                    Hit <strong>Complete Setup</strong> and Kivo will start keeping you in the loop.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            data-testid="onboarding-back"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          {currentStep < 6 ? (
            <Button
              onClick={handleNext}
              className="bg-primary hover:bg-primary-800"
              data-testid="onboarding-next"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={saving}
              className="bg-primary hover:bg-primary-800"
              data-testid="onboarding-complete"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete Setup
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentOnboarding;
